import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, FileCheck, RefreshCw, Package, Settings, 
  FolderOpen, Layers, DollarSign, Receipt
} from 'lucide-react';
import AdminContractsPage from './AdminContractsPage';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BILLING_MENU = [
  { id: 'quotes', label: 'Quotes', icon: FileText, description: 'Quote builder & management' },
  { id: 'invoices', label: 'Invoices', icon: Receipt, description: 'Invoice management' },
  { id: 'recurring', label: 'Recurring', icon: RefreshCw, description: 'Recurring billing' },
  { id: 'categories', label: 'Categories', icon: FolderOpen, description: 'Product/service categories' },
  { id: 'settings', label: 'Settings', icon: Settings, description: 'Billing settings' },
  { id: 'products', label: 'Products', icon: Package, description: 'Product catalog' },
  { id: 'services', label: 'Services', icon: Layers, description: 'Service catalog' },
  { id: 'contracts', label: 'Contracts', icon: FileCheck, description: 'Contract templates' },
];

export default function BillingDashboard() {
  const { token, user } = useAuth();
  const [activeSection, setActiveSection] = useState('quotes');
  const [stats, setStats] = useState({
    totalQuotes: 0,
    signedQuotes: 0,
    totalInvoices: 0,
    paidInvoices: 0,
    totalProducts: 0,
    totalServices: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch stats from various endpoints
      const [quotesRes, invoicesRes, productsRes, servicesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/billing/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/invoices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/services`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats({
        totalQuotes: quotesRes.status === 'fulfilled' ? quotesRes.value.data?.total_quotes || 0 : 0,
        signedQuotes: quotesRes.status === 'fulfilled' ? quotesRes.value.data?.signed_quotes || 0 : 0,
        totalInvoices: invoicesRes.status === 'fulfilled' ? invoicesRes.value.data?.invoices?.length || 0 : 0,
        paidInvoices: invoicesRes.status === 'fulfilled' ? 
          invoicesRes.value.data?.invoices?.filter(i => i.status === 'paid')?.length || 0 : 0,
        totalProducts: productsRes.status === 'fulfilled' ? productsRes.value.data?.products?.length || 0 : 0,
        totalServices: servicesRes.status === 'fulfilled' ? servicesRes.value.data?.services?.length || 0 : 0
      });
    } catch (error) {
      console.log('Stats not available');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'quotes':
        return <QuotesDashboard />;
      case 'invoices':
        return <InvoicesDashboard />;
      case 'recurring':
        return <RecurringDashboard />;
      case 'categories':
        return <CategoriesDashboard />;
      case 'settings':
        return <BillingSettings />;
      case 'products':
        return <ProductsDashboard />;
      case 'services':
        return <ServicesDashboard />;
      case 'contracts':
        return <AdminContractsPage />;
      default:
        return <QuotesDashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="billing-dashboard">
      <div className="flex">
        {/* Left Sidebar Menu */}
        <div className="w-64 bg-white border-r border-gray-200 min-h-screen p-4">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-[#0A1628] flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-[#84CC16]" />
              Billing
            </h2>
            <p className="text-sm text-gray-500">Quotes, invoices & payments</p>
          </div>

          <nav className="space-y-1">
            {BILLING_MENU.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                    isActive 
                      ? 'bg-[#84CC16]/10 text-[#84CC16] font-medium' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`billing-menu-${item.id}`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-[#84CC16]' : 'text-gray-400'}`} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Quick Stats */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Stats</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Quotes</span>
                <span className="font-medium text-[#0A1628]">{stats.totalQuotes}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Signed</span>
                <span className="font-medium text-green-600">{stats.signedQuotes}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Products</span>
                <span className="font-medium text-[#0A1628]">{stats.totalProducts}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Services</span>
                <span className="font-medium text-[#0A1628]">{stats.totalServices}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// Quotes Dashboard - Overview of all quotes
function QuotesDashboard() {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    fetchLeadsWithQuotes();
  }, []);

  const fetchLeadsWithQuotes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leads`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setLeads(res.data.leads || []);
    } catch (error) {
      console.log('No leads found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Quotes Overview</h1>
        <p className="text-gray-500">Manage quotes for all leads. Select a lead to create or view quotes.</p>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 text-xs font-medium text-gray-500 border-b">
            <div className="col-span-4">Lead / Company</div>
            <div className="col-span-3">Contact</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          {leads.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              No leads found. Create leads first to generate quotes.
            </div>
          ) : (
            leads.slice(0, 20).map((lead) => (
              <div key={lead.id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50">
                <div className="col-span-4">
                  <p className="font-medium text-[#0A1628]">{lead.company_name || lead.company || 'No Company'}</p>
                  <p className="text-sm text-gray-500">{lead.first_name} {lead.last_name}</p>
                </div>
                <div className="col-span-3">
                  <p className="text-sm text-gray-600">{lead.email}</p>
                  <p className="text-sm text-gray-400">{lead.phone}</p>
                </div>
                <div className="col-span-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    lead.status === 'qualified' ? 'bg-green-100 text-green-700' :
                    lead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {lead.status || 'new'}
                  </span>
                </div>
                <div className="col-span-3 text-right">
                  <button
                    onClick={() => setSelectedLead(lead)}
                    className="text-sm text-[#014DB7] hover:text-blue-700 font-medium"
                  >
                    Manage Quotes →
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Quote Builder Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full my-8">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-lg font-semibold text-[#0A1628]">
                  Quotes for {selectedLead.company_name || `${selectedLead.first_name} ${selectedLead.last_name}`}
                </h2>
                <p className="text-sm text-gray-500">{selectedLead.email}</p>
              </div>
              <button
                onClick={() => setSelectedLead(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <QuoteBuilderEmbed leadId={selectedLead.id} lead={selectedLead} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Embedded quote builder without needing to import the full page
function QuoteBuilderEmbed({ leadId, lead }) {
  // Import the actual QuoteBuilderPage dynamically
  const [QuoteBuilder, setQuoteBuilder] = useState(null);

  useEffect(() => {
    import('./QuoteBuilderPage').then(module => {
      setQuoteBuilder(() => module.default);
    });
  }, []);

  if (!QuoteBuilder) {
    return (
      <div className="text-center py-8 text-gray-500">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
        Loading quote builder...
      </div>
    );
  }

  return <QuoteBuilder leadId={leadId} lead={lead} />;
}

// Placeholder components for other sections
function InvoicesDashboard() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Invoices</h1>
        <p className="text-gray-500">Manage invoices and track payments.</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="font-semibold text-gray-700 mb-2">Invoice Management</h3>
        <p className="text-gray-500">Invoices are automatically created when quotes are signed. View and manage them here.</p>
      </div>
    </div>
  );
}

function RecurringDashboard() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Recurring Billing</h1>
        <p className="text-gray-500">Manage subscriptions and recurring charges.</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="font-semibold text-gray-700 mb-2">Recurring Billing</h3>
        <p className="text-gray-500">Set up and manage recurring billing cycles for monthly and yearly services.</p>
      </div>
    </div>
  );
}

function CategoriesDashboard() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Categories</h1>
        <p className="text-gray-500">Organize products and services into categories.</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="font-semibold text-gray-700 mb-2">Category Management</h3>
        <p className="text-gray-500">Create and manage categories to organize your product and service catalog.</p>
      </div>
    </div>
  );
}

function BillingSettings() {
  const { token } = useAuth();
  const [settings, setSettings] = useState({
    invoice_prefix: 'INV',
    invoice_next_number: 1001,
    default_payment_terms: 30,
    tax_rate: 0,
    company_name: '',
    company_address: '',
    company_phone: ''
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Billing Settings</h1>
        <p className="text-gray-500">Configure billing preferences and defaults.</p>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Prefix</label>
            <input
              type="text"
              value={settings.invoice_prefix}
              onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="INV"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Next Invoice Number</label>
            <input
              type="number"
              value={settings.invoice_next_number}
              onChange={(e) => setSettings({ ...settings, invoice_next_number: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Payment Terms (days)</label>
            <input
              type="number"
              value={settings.default_payment_terms}
              onChange={(e) => setSettings({ ...settings, default_payment_terms: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
            <input
              type="number"
              step="0.01"
              value={settings.tax_rate}
              onChange={(e) => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>
        <div className="pt-4 border-t">
          <button className="px-4 py-2 bg-[#84CC16] text-white rounded-lg hover:bg-[#65a30d]">
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductsDashboard() {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/products`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setProducts(res.data.products || []);
    } catch (error) {
      console.log('No products found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Products</h1>
          <p className="text-gray-500">Manage your product catalog for quotes.</p>
        </div>
        <button className="px-4 py-2 bg-[#84CC16] text-white rounded-xl hover:bg-[#65a30d] flex items-center gap-2">
          <Package className="w-4 h-4" /> Add Product
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-700 mb-2">No Products Yet</h3>
          <p className="text-gray-500">Add products to your catalog to include them in quotes.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 text-xs font-medium text-gray-500 border-b">
            <div className="col-span-4">Product</div>
            <div className="col-span-2">SKU</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {products.map((product) => (
            <div key={product.id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50">
              <div className="col-span-4">
                <p className="font-medium text-[#0A1628]">{product.name}</p>
                <p className="text-sm text-gray-500 line-clamp-1">{product.description}</p>
              </div>
              <div className="col-span-2 text-sm text-gray-600">{product.sku || '-'}</div>
              <div className="col-span-2 text-sm text-gray-600">{product.category || '-'}</div>
              <div className="col-span-2">
                <p className="text-sm font-medium">{formatCurrency(product.price_onetime || product.price)}</p>
                {product.price_monthly > 0 && (
                  <p className="text-xs text-blue-600">{formatCurrency(product.price_monthly)}/mo</p>
                )}
              </div>
              <div className="col-span-2 text-right">
                <button className="text-sm text-[#014DB7] hover:text-blue-700">Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServicesDashboard() {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/services`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setServices(res.data.services || []);
    } catch (error) {
      console.log('No services found');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">Services</h1>
          <p className="text-gray-500">Manage your service catalog for quotes.</p>
        </div>
        <button className="px-4 py-2 bg-[#84CC16] text-white rounded-xl hover:bg-[#65a30d] flex items-center gap-2">
          <Layers className="w-4 h-4" /> Add Service
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading...
        </div>
      ) : services.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Layers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-700 mb-2">No Services Yet</h3>
          <p className="text-gray-500">Add services to your catalog to include them in quotes.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 text-xs font-medium text-gray-500 border-b">
            <div className="col-span-4">Service</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">One-Time</div>
            <div className="col-span-2">Monthly</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {services.map((service) => (
            <div key={service.id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b hover:bg-gray-50">
              <div className="col-span-4">
                <p className="font-medium text-[#0A1628]">{service.name}</p>
                <p className="text-sm text-gray-500 line-clamp-1">{service.description}</p>
              </div>
              <div className="col-span-2 text-sm text-gray-600">{service.category || '-'}</div>
              <div className="col-span-2 text-sm font-medium">
                {service.price_onetime > 0 ? formatCurrency(service.price_onetime) : '-'}
              </div>
              <div className="col-span-2 text-sm font-medium text-blue-600">
                {service.price_monthly > 0 ? `${formatCurrency(service.price_monthly)}/mo` : '-'}
              </div>
              <div className="col-span-2 text-right">
                <button className="text-sm text-[#014DB7] hover:text-blue-700">Edit</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
